<template>
  <v-container
    fluid
    class="teacher-timetable pa-2 pa-md-4"
  >
    <v-row align="center">
      <v-col
        cols="12"
        md="3"
      >
        <div class="text-h6 font-weight-bold">Расписание преподавателей</div>
      </v-col>

      <v-col
        cols="12"
        md="6"
        class="d-flex align-center justify-center"
      >
        <v-btn
          icon="mdi-chevron-left"
          size="small"
          variant="text"
          aria-label="Предыдущая неделя"
          :disabled="loadingTimetable"
          @click="changeWeek(-1)"
        />

        <v-btn
          class="mx-1 text-none"
          variant="text"
          :disabled="loadingTimetable"
          @click="calendarVisible = true"
        >
          <v-icon start>mdi-calendar</v-icon>
          {{ weekTitle }}
        </v-btn>

        <v-btn
          icon="mdi-chevron-right"
          size="small"
          variant="text"
          aria-label="Следующая неделя"
          :disabled="loadingTimetable"
          @click="changeWeek(1)"
        />
      </v-col>

      <v-col
        cols="12"
        md="3"
        class="d-flex justify-md-end"
      >
        <v-btn
          color="success"
          prepend-icon="mdi-microsoft-excel"
          :disabled="effectiveSelectedTeacherIds.length === 0 || printing"
          :loading="printing"
          @click="printTimetable"
        >
          Печать
        </v-btn>
      </v-col>
    </v-row>

    <v-row
      align="center"
      class="my-6"
    >
      <v-col
        v-if="showDepartmentFilter"
        cols="12"
        md="3"
      >
        <v-select
          v-model="selectedDepartmentId"
          :items="departments"
          item-title="name"
          item-value="id"
          label="Комплекс"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          @update:model-value="departmentChanged"
        />
      </v-col>

      <v-col
        cols="12"
        :md="showDepartmentFilter ? 7 : 10"
      >
        <v-autocomplete
          v-model="selectedTeacherIds"
          :items="teacherFilterItems"
          item-title="name"
          item-value="id"
          label="Преподаватели"
          placeholder="Выберите одного или нескольких преподавателей"
          variant="outlined"
          density="comfortable"
          multiple
          chips
          closable-chips
          clearable
          hide-details="auto"
          no-data-text="В выбранном комплексе преподаватели не найдены"
          :disabled="availableTeachers.length === 0"
          @update:model-value="teacherSelectionChanged"
        />
      </v-col>

      <v-col
        cols="12"
        md="2"
        class="d-flex justify-md-end"
      >
        <v-btn
          color="primary"
          prepend-icon="mdi-magnify"
          :disabled="
            effectiveSelectedTeacherIds.length === 0 || loadingTimetable
          "
          :loading="loadingTimetable"
          @click="loadTimetable"
        >
          Показать
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="errorMessage"
      class="mb-4"
      type="error"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-else-if="!loadingPageData && teachers.length === 0"
      class="mb-4"
      type="info"
      variant="tonal"
    >
      Справочник преподавателей пока не подключён.
    </v-alert>

    <v-skeleton-loader
      v-if="loadingPageData || loadingTimetable"
      type="heading, table"
    />

    <v-card
      v-else-if="effectiveSelectedTeacherIds.length === 0"
      variant="outlined"
    >
      <v-empty-state
        icon="mdi-account-multiple-outline"
        title="Выберите преподавателей"
        text="После выбора здесь появится их расписание на указанную неделю."
      />
    </v-card>

    <v-card
      v-else-if="!timetableLoaded"
      variant="outlined"
    >
      <v-empty-state
        icon="mdi-filter-check-outline"
        title="Фильтры выбраны"
        text="Нажмите «Показать», чтобы загрузить расписание."
      />
    </v-card>

    <div
      v-else
      class="d-flex flex-column ga-4"
    >
      <v-card
        v-for="teacher in displayedTimetables"
        :key="teacher.teacher_id"
        elevation="1"
      >
        <v-card-title
          class="teacher-card-header d-flex align-center ga-2"
          role="button"
          tabindex="0"
          :aria-expanded="!collapsedTeacherIds.includes(Number(teacher.teacher_id))"
          @click="toggleTeacherCard(teacher.teacher_id)"
          @keydown.enter.prevent="toggleTeacherCard(teacher.teacher_id)"
          @keydown.space.prevent="toggleTeacherCard(teacher.teacher_id)"
        >
          <v-icon color="primary">mdi-account-school-outline</v-icon>
          <span class="text-truncate">{{ teacher.teacher_name }}</span>
          <v-spacer />
          <v-icon>
            {{
              collapsedTeacherIds.includes(Number(teacher.teacher_id))
                ? "mdi-chevron-down"
                : "mdi-chevron-up"
            }}
          </v-icon>
        </v-card-title>

        <v-expand-transition>
          <div v-show="!collapsedTeacherIds.includes(Number(teacher.teacher_id))">
            <v-divider />

            <v-card-text>
              <v-row density="compact">
                <v-col
                  v-for="day in teacher.days"
                  :key="day.date"
                  cols="12"
                  sm="12"
                  lg="6"
                  xl="4"
                >
                  <v-card
                    class="h-100 rounded-xl"
                    variant="outlined"
                  >
                    <v-card-item
                      :class="{ 'bg-primary text-white': isToday(day.date) }"
                    >
                      <v-card-title class="text-subtitle-1 font-weight-bold">
                        {{ formatDay(day.date) }}
                      </v-card-title>
                    </v-card-item>

                    <v-divider class="my-0" />

                    <v-card-text class="pa-0">
                      <div
                        v-if="day.lessonGroups.length === 0"
                        class="text-medium-emphasis text-center py-5"
                      >
                        Занятий нет
                      </div>

                      <div
                        v-for="lessonGroup in day.lessonGroups"
                        :key="lessonGroup.key"
                        class="lesson d-flex align-stretch"
                      >
                        <div
                          class="lesson-time d-flex flex-column align-center justify-center border-e bg-grey-lighten-5"
                        >
                          <span class="font-weight-black">
                            {{ lessonGroup.lesson_start || "?" }}
                          </span>
                          <span class="text-medium-emphasis">
                            {{ lessonGroup.lesson_end || "?" }}
                          </span>
                        </div>

                        <div class="lesson-content lesson-lines flex-grow-1">
                          <div
                            v-for="lesson in lessonGroup.lessons"
                            :key="lesson.id"
                            class="lesson-line lesson-grid"
                          >
                            <template v-if="!lesson.empty">
                              <a
                                v-if="lesson.course_id"
                                class="lesson-discipline lesson-course-link"
                                :href="$model.wwwroot + '/course/view.php?id=' + lesson.course_id"
                                target="_blank"
                                rel="noopener noreferrer"
                                :title="
                                  'Открыть электронный курс: ' +
                                  (lesson.discipline_short ||
                                    lesson.discipline ||
                                    'Дисциплина')
                                "
                                @click.stop
                              >
                                {{
                                  lesson.discipline_short ||
                                  lesson.discipline ||
                                  "Дисциплина"
                                }}
                              </a>
                              <span
                                v-else
                                class="lesson-discipline"
                                :title="
                                  lesson.discipline_short ||
                                  lesson.discipline ||
                                  'Дисциплина'
                                "
                              >
                                {{
                                  lesson.discipline_short ||
                                  lesson.discipline ||
                                  "Дисциплина"
                                }}
                              </span>
                              <span
                                v-if="lesson.group_name"
                                class="lesson-group text-caption text-medium-emphasis"
                              >
                                {{ lesson.group_name }}
                              </span>

                              <v-chip
                                v-if="lesson.classroom"
                                size="x-small"
                                variant="tonal"
                                class="flex-shrink-0"
                              >
                                {{ lesson.classroom }}
                              </v-chip>
                            </template>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <v-dialog
      v-model="calendarVisible"
      width="auto"
    >
      <v-card>
        <v-locale-provider locale="ru">
          <v-date-picker
            v-model="calendarDate"
            :min="semester.begin || undefined"
            :max="semester.end || undefined"
            hide-header
            @update:model-value="dateSelected"
          />
        </v-locale-provider>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="calendarVisible = false"
            >Отмена</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "TeacherTimetablePage",

  data() {
    return {
      departments: [],
      teachers: [],
      semester: {
        begin: null,
        end: null,
      },
      selectedDepartmentId: null,
      selectedTeacherIds: [],
      allTeachersValue: "all",
      selectedDate: new Date(),
      calendarDate: new Date(),
      calendarVisible: false,
      timetableItems: [],
      loadedTeacherIds: [],
      collapsedTeacherIds: [],
      timetableLoaded: false,
      loadingPageData: false,
      loadingTimetable: false,
      printing: false,
      errorMessage: "",
    };
  },

  computed: {
    availableTeachers() {
      if (Number(this.selectedDepartmentId) === 0) {
        return this.teachers;
      }

      return this.teachers.filter((teacher) => {
        return (teacher.department_ids || [])
          .map(Number)
          .includes(Number(this.selectedDepartmentId));
      });
    },

    showDepartmentFilter() {
      return this.departments.filter((department) => Number(department.id) > 0)
        .length > 1;
    },

    teacherFilterItems() {
      if (this.availableTeachers.length === 0) {
        return [];
      }

      return [
        {
          id: this.allTeachersValue,
          name: "Все",
        },
        ...this.availableTeachers,
      ];
    },

    effectiveSelectedTeacherIds() {
      if (this.selectedTeacherIds.includes(this.allTeachersValue)) {
        return this.availableTeachers.map((teacher) => Number(teacher.id));
      }

      return this.selectedTeacherIds.map(Number).filter(Number.isInteger);
    },

    weekDays() {
      const selected = new Date(this.selectedDate);
      const mondayOffset = (selected.getDay() + 6) % 7;
      const monday = new Date(
        selected.getFullYear(),
        selected.getMonth(),
        selected.getDate() - mondayOffset,
      );

      return Array.from({ length: 6 }, (_, index) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + index);

        return {
          date: this.formatDateForRequest(day),
          lessons: [],
        };
      });
    },

    weekTitle() {
      const firstDay = this.weekDays[0].date;
      const lastDay = this.weekDays[this.weekDays.length - 1].date;
      const start = this.parseLocalDate(firstDay).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
      });
      const end = this.parseLocalDate(lastDay).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      return `${start} — ${end}`;
    },

    displayedTimetables() {
      return this.loadedTeacherIds.map((teacherId) => {
        const teacher = this.teachers.find(
          (item) => Number(item.id) === Number(teacherId),
        );
        const timetable = this.timetableItems.find(
          (item) => Number(item.teacher_id) === Number(teacherId),
        );
        const timetableDays = new Map(
          (timetable?.days || []).map((day) => [day.date, day.lessons || []]),
        );

        return {
          teacher_id: teacherId,
          teacher_name:
            timetable?.teacher_name ||
            teacher?.name ||
            `Преподаватель #${teacherId}`,
          days: this.weekDays.map((day) => {
            const lessons = (timetableDays.get(day.date) || []).filter(
              (lesson) => lesson.cancel != 1,
            );
            const lessonGroups = [];
            const lessonGroupsByTime = new Map();

            lessons.forEach((lesson) => {
              const hasTime = lesson.lesson_start || lesson.lesson_end;
              const groupKey = hasTime
                ? `time:${lesson.lesson_start || ""}|${lesson.lesson_end || ""}`
                : `lesson:${lesson.lesson}`;

              if (!lessonGroupsByTime.has(groupKey)) {
                const lessonGroup = {
                  key: `${day.date}-${groupKey}`,
                  lesson_start: lesson.lesson_start,
                  lesson_end: lesson.lesson_end,
                  lessons: [],
                };
                lessonGroupsByTime.set(groupKey, lessonGroup);
                lessonGroups.push(lessonGroup);
              }

              lessonGroupsByTime.get(groupKey).lessons.push(lesson);
            });

            return {
              date: day.date,
              lessonGroups,
            };
          }),
        };
      });
    },
  },

  created() {
    this.loadPageData();
  },

  mounted() {
    document.getElementById("teacher-timetable-favicon")?.remove();
    const favicon = document.createElement("link");
    favicon.id = "teacher-timetable-favicon";
    favicon.rel = "icon";
    favicon.type = "image/svg+xml";
    favicon.href = `${this.$model.wwwroot}/procollege/lib/image/mdi-calendar-account.svg`;
    document.head.appendChild(favicon);
  },

  beforeUnmount() {
    document.getElementById("teacher-timetable-favicon")?.remove();
  },

  methods: {
    loadPageData() {
      this.loadingPageData = true;
      this.errorMessage = "";

      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/timetable2/teacher/getPageData",
        )
        .then((response) => {
          const departments = Object.values(response.data.departments || {})
            .map((department) => ({
              id: Number(department.id),
              name:
                department.name ||
                department.short_name ||
                `Комплекс #${department.id}`,
            }))
            .filter(
              (department) =>
                Number.isInteger(department.id) && department.id > 0,
            );
          this.departments = [
            {
              id: 0,
              name: "Все",
            },
            ...departments,
          ];

          this.teachers = Object.values(response.data.teachers || {})
            .map((teacher) => ({
              id: Number(teacher.id),
              last_name: teacher.last_name || "",
              first_name: teacher.name || "",
              middle_name: teacher.middle_name || "",
              name:
                [teacher.last_name, teacher.name, teacher.middle_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || `Преподаватель #${teacher.id}`,
              department_ids: Array.isArray(teacher.department_ids)
                ? teacher.department_ids
                    .map(Number)
                    .filter((id) => Number.isInteger(id) && id > 0)
                : String(teacher.department_ids || "")
                    .split(",")
                    .filter((id) => id.trim() !== "")
                    .map((id) => Number(id.trim()))
                    .filter((id) => Number.isInteger(id) && id > 0),
            }))
            .filter((teacher) => Number.isInteger(teacher.id) && teacher.id > 0)
            .sort((firstTeacher, secondTeacher) => {
              return (
                firstTeacher.last_name.localeCompare(
                  secondTeacher.last_name,
                  "ru",
                  { sensitivity: "base" },
                ) ||
                firstTeacher.first_name.localeCompare(
                  secondTeacher.first_name,
                  "ru",
                  { sensitivity: "base" },
                ) ||
                firstTeacher.middle_name.localeCompare(
                  secondTeacher.middle_name,
                  "ru",
                  { sensitivity: "base" },
                )
              );
            });

          this.semester = response.data.semester || { begin: null, end: null };

          if (this.departments.length > 0) {
            this.selectedDepartmentId = this.departments[0].id;
          }
        })
        .catch((error) => {
          this.errorMessage =
            error.response?.data?.error ||
            "Не удалось загрузить справочники расписания.";
        })
        .finally(() => {
          this.loadingPageData = false;
        });
    },

    departmentChanged() {
      if (this.selectedTeacherIds.includes(this.allTeachersValue)) {
        this.timetableItems = [];
        this.loadedTeacherIds = [];
        this.timetableLoaded = false;
        return;
      }

      const availableIds = new Set(
        this.availableTeachers.map((teacher) => Number(teacher.id)),
      );
      this.selectedTeacherIds = this.selectedTeacherIds.filter((id) =>
        availableIds.has(Number(id)),
      );
      this.timetableItems = [];
      this.loadedTeacherIds = [];
      this.timetableLoaded = false;
    },

    teacherSelectionChanged(values) {
      const selectedValues = Array.isArray(values) ? values : [];

      if (selectedValues.includes(this.allTeachersValue)) {
        this.selectedTeacherIds = [this.allTeachersValue];
      } else {
        const availableIds = new Set(
          this.availableTeachers.map((teacher) => Number(teacher.id)),
        );
        this.selectedTeacherIds = selectedValues
          .map(Number)
          .filter((id) => availableIds.has(id));
      }

      this.timetableItems = [];
      this.loadedTeacherIds = [];
      this.timetableLoaded = false;
    },

    toggleTeacherCard(teacherId) {
      const normalizedTeacherId = Number(teacherId);
      if (this.collapsedTeacherIds.includes(normalizedTeacherId)) {
        this.collapsedTeacherIds = this.collapsedTeacherIds.filter(
          (id) => id !== normalizedTeacherId,
        );
      } else {
        this.collapsedTeacherIds = [
          ...this.collapsedTeacherIds,
          normalizedTeacherId,
        ];
      }
    },

    changeWeek(direction) {
      const shouldReload = this.timetableLoaded;
      const nextDate = new Date(this.selectedDate);
      nextDate.setDate(nextDate.getDate() + direction * 7);
      this.selectedDate = nextDate;
      this.calendarDate = new Date(nextDate);

      if (shouldReload) {
        this.loadTimetable();
      }
    },

    dateSelected(value) {
      if (!value) {
        return;
      }

      const shouldReload = this.timetableLoaded;
      this.selectedDate = new Date(value);
      this.calendarDate = new Date(value);
      this.calendarVisible = false;

      if (shouldReload) {
        this.loadTimetable();
      }
    },

    loadTimetable() {
      this.errorMessage = "";
      this.timetableItems = [];
      this.loadedTeacherIds = [];
      this.timetableLoaded = false;

      if (this.effectiveSelectedTeacherIds.length === 0) {
        return;
      }

      const requestedTeacherIds = [...this.effectiveSelectedTeacherIds];
      this.loadingTimetable = true;
      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/timetable2/teacher/getTimetable",
          new URLSearchParams({
            date: this.formatDateForRequest(this.selectedDate),
            department_id: this.selectedDepartmentId || 0,
            teacher_ids: JSON.stringify(requestedTeacherIds),
          }),
        )
        .then((response) => {
          this.timetableItems = response.data.items || [];
          this.loadedTeacherIds = requestedTeacherIds;
          this.timetableLoaded = true;
        })
        .catch((error) => {
          this.errorMessage =
            error.response?.data?.error || "Не удалось загрузить расписание.";
        })
        .finally(() => {
          this.loadingTimetable = false;
        });
    },

    printTimetable() {
      this.printing = true;
      this.errorMessage = "";

      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/timetable2/teacher/printTimetable",
          new URLSearchParams({
            date: this.formatDateForRequest(this.selectedDate),
            department_id: this.selectedDepartmentId || 0,
            teacher_ids: JSON.stringify(this.effectiveSelectedTeacherIds),
          }),
          { responseType: "blob" },
        )
        .then((response) => {
          const url = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = `teacher-timetable-${this.weekDays[0].date}.xlsx`;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.errorMessage = "Не удалось сформировать файл расписания.";
        })
        .finally(() => {
          this.printing = false;
        });
    },

    formatDateForRequest(dateValue) {
      const date = new Date(dateValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    parseLocalDate(dateValue) {
      const [year, month, day] = dateValue.split("-").map(Number);
      return new Date(year, month - 1, day);
    },

    formatDay(dateValue) {
      const formatted = this.parseLocalDate(dateValue).toLocaleDateString(
        "ru-RU",
        {
          weekday: "short",
          day: "numeric",
          month: "short",
        },
      );

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },

    isToday(dateValue) {
      return dateValue === this.formatDateForRequest(new Date());
    },
  },
};
</script>

<style scoped>
.teacher-timetable {
  max-width: 1920px;
}

.teacher-card-header {
  cursor: pointer;
  user-select: none;
}

.lesson-discipline {
  display: -webkit-box;
  overflow: hidden;
  min-width: 0;
  line-height: 1.25;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.lesson-course-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.lesson-course-link:hover,
.lesson-course-link:focus-visible {
  text-decoration: underline;
}

.lesson-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content max-content;
  align-items: center;
  gap: 8px;
}

.lesson-group {
  white-space: nowrap;
}

.lesson {
  min-height: 50px;
}

.lesson:not(:last-child) {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.lesson-lines {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.lesson-line {
  min-width: 0;
}

.lesson-time {
  flex: 0 0 74px;
  line-height: 1.25;
  padding: 8px;
  white-space: nowrap;
}

.lesson-content {
  min-width: 0;
  padding: 8px 12px;
}
</style>
